import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import { Activity, ActivityComment } from './_types';

interface FetchActivitiesOptions {
  userId?: string;
  page?: number;
  perPage?: number;
  lastItemId?: Identifier;
}

// New: Group discussion fetch options
export interface FetchGroupActivitiesOptions {
  groupId: string;
  userId?: string;
  page?: number;
  perPage?: number;
  lastItemId?: Identifier;
}

export type ActivityType =
  | 'user'
  | 'following'
  | 'clients'
  | 'all'
  | 'group_discussion'
  | 'group'
  | 'public'
  | 'following_with_user';

// Internal base fetcher to DRY user and group_discussion activity fetching
interface FetchActivitiesBaseOptions {
  activityType: ActivityType;
  userId?: string;
  groupId?: string;
  page?: number;
  perPage?: number;
  lastItemId?: Identifier;
  types?: string;
}

const fetchActivitiesBase = async ({
  activityType,
  userId,
  groupId,
  page = 1,
  perPage = 10,
  lastItemId,
  types,
}: FetchActivitiesBaseOptions): Promise<Activity[]> => {
  const params: Record<string, unknown> = {
    activity_type: activityType,
    user_id: userId,
    group_id: groupId,
    page,
    per: perPage,
    last_item: lastItemId,
  };
  if (types) params.types = types;

  const response = await api.get('/api/v2/activities', { params });

  return response.data.map((activity: unknown) =>
    camelcaseKeys(activity, { deep: true })
  );
};

export const fetchActivities = async ({
  userId,
  page = 1,
  perPage = 10,
  lastItemId,
}: FetchActivitiesOptions = {}): Promise<Activity[]> =>
  fetchActivitiesBase({
    activityType: 'user',
    userId,
    page,
    perPage,
    lastItemId,
  });

export const fetchActivityById = async (
  activityId: string | number
): Promise<Activity> => {
  const response = await api.get(`/api/v2/activities/${activityId}`);
  return camelcaseKeys(response.data, { deep: true });
};

export interface FetchDashboardActivitiesOptions {
  activityType: ActivityType;
  userId?: string;
  page?: number;
  perPage?: number;
  lastItemId?: Identifier;
  groupId?: string;
  types?: string;
}

export const fetchDashboardActivities = async ({
  activityType,
  userId,
  page = 1,
  perPage = 10,
  lastItemId,
  groupId,
  types,
}: FetchDashboardActivitiesOptions): Promise<Activity[]> => {
  return fetchActivitiesBase({
    activityType,
    userId,
    page,
    perPage,
    lastItemId,
    groupId,
    types,
  });
};

// New: Fetch activities for Group Discussion feed
export const fetchGroupActivities = async ({
  groupId,
  userId,
  page = 1,
  perPage = 10,
  lastItemId,
}: FetchGroupActivitiesOptions): Promise<Activity[]> =>
  fetchActivitiesBase({
    activityType: 'group_discussion',
    groupId,
    userId,
    page,
    perPage,
    lastItemId,
  });

// New: Fetch scheduled (unpublished) activities for a group
export const fetchGroupScheduledActivities = async (
  groupId: string,
  { perPage = 25 }: { perPage?: number } = {}
): Promise<Activity[]> => {
  const response = await api.get('/api/v2/activities', {
    params: {
      activity_type: 'group_discussion',
      group_id: groupId,
      per: perPage,
      scheduled_only: 1,
    },
  });

  return response.data.map((activity: unknown) =>
    camelcaseKeys(activity, { deep: true })
  );
};

// Payload for creating a group discussion post
export interface CreateGroupDiscussionPostPayload {
  groupId: string;
  html: string;
  imageSignedIds?: string[];
  videoIds?: string[];
  voiceMemoSignedId?: string;
  videoUploads?: Array<{ key: string; match: string }>;
  publishAt?: Date | string | null;
}

// New: Create a group discussion post (named params)
export const createGroupDiscussionPost = async ({
  groupId,
  html,
  imageSignedIds = [],
  videoIds = [],
  voiceMemoSignedId,
  videoUploads,
  publishAt,
}: CreateGroupDiscussionPostPayload): Promise<Activity> => {
  const payload: any = { data: html };
  if (voiceMemoSignedId) payload.voice_memo = voiceMemoSignedId;
  if (imageSignedIds.length > 0) payload.images = imageSignedIds;
  if (videoIds.length > 0) payload.video_ids = videoIds;
  if (videoUploads && videoUploads.length > 0) payload.videos = videoUploads;
  if (publishAt)
    payload.publish_at =
      typeof publishAt === 'string' ? publishAt : publishAt.toISOString();
  const body: any = { activity: payload };
  const response = await api.post(`/groups/${groupId}/updates/`, body);

  return camelcaseKeys(response.data, { deep: true });
};

export const addActivityComment = async (
  activityId: string,
  comment: string
): Promise<ActivityComment> => {
  const response = await api.post(
    `/api/v2/activities/${activityId}/comment`,
    decamelizeKeys({ comment })
  );

  return camelcaseKeys(response.data, { deep: true });
};

export const toggleActivityLike = async (
  activityId: string,
  liked: boolean
): Promise<void> => {
  if (liked) {
    await api.delete(`/api/v2/activities/${activityId}/remove_high_five`);
  } else {
    await api.post(`/api/v2/activities/${activityId}/high_five`);
  }
};

// New: Toggle pin on an activity (admin-only)
export const togglePinActivity = async (
  activityId: string
): Promise<{ pinned: boolean }> => {
  const response = await api.post(
    `/api/v2/activities/${activityId}/toggle_pin`
  );
  return camelcaseKeys(response.data, { deep: true });
};

// New: Toggle disable comments on an activity (admin-only)
export const toggleDisableComments = async (
  activityId: string
): Promise<{ disableComments: boolean }> => {
  const response = await api.post(
    `/api/v2/activities/${activityId}/toggle_disable_comments`
  );
  return camelcaseKeys(response.data, { deep: true });
};

// New: Edit the HTML/string content of an activity (owner-only for type 16)
export const editActivityPost = async (
  activityId: string,
  html: string
): Promise<Activity> => {
  const response = await api.put(`/api/v2/activities/${activityId}/`, {
    data: html,
  });
  return camelcaseKeys(response.data, { deep: true });
};

// New: Delete an activity post (admin or owner)
export const deleteActivityPost = async (activityId: string): Promise<void> => {
  await api.delete(`/api/v2/activities/${activityId}/`);
};

// New: Delete a specific comment from an activity (admin or owner)
export const deleteActivityComment = async (
  activityId: string,
  commentId: string | number
): Promise<void> => {
  await api.post(
    `/api/v2/activities/${activityId}/delete_comment/`,
    decamelizeKeys({ commentId })
  );
};

// Update a group discussion post (allows updating content and publish_at for scheduled items)
export const updateGroupDiscussionPost = async ({
  id,
  html,
  publishAt,
}: {
  id: Identifier | string | number;
  html?: string;
  publishAt?: Date | string | null;
}): Promise<Activity> => {
  const body: any = {};
  if (typeof html === 'string') body.data = html;
  if (publishAt !== undefined)
    body.publish_at =
      publishAt && typeof publishAt !== 'string'
        ? publishAt.toISOString()
        : publishAt || undefined;
  const response = await api.put(`/api/v2/activities/${id}/`, body);
  return camelcaseKeys(response.data, { deep: true });
};
